import { ChevronRight, type LucideIcon } from 'lucide-react';

export interface ActionTileProps {
  id: string;
  icon: LucideIcon;
  label: string;
  description: string;
  onClick?: () => void;
  disabled?: boolean;
  badge?: string;
  /** `action` = laranja de fila pendente. Default = cinza (ou azul se `highlighted`). */
  badgeTone?: 'default' | 'action';
  accent?: string;
  iconBg?: string;
  ringFocus?: string;
  variant?: 'compact' | 'hero';
  index?: number;
  /** Indicador de status (ex.: tela online). */
  statusOnline?: boolean;
  showStatus?: boolean;
  ctaLabel?: string;
  /** Destaque visual (ex.: atalho recomendado com TV online). */
  highlighted?: boolean;
}

const DEFAULT_ACCENT =
  'border-white/[0.06] hover:border-white/20 hover:bg-white/[0.04]';
const DEFAULT_ICON_BG = 'bg-white/5 text-slate-400';
const DEFAULT_RING = 'focus-visible:ring-white/25';

export function ActionTile({
  id,
  icon: Icon,
  label,
  description,
  onClick,
  disabled = false,
  badge,
  badgeTone = 'default',
  accent = DEFAULT_ACCENT,
  iconBg = DEFAULT_ICON_BG,
  ringFocus = DEFAULT_RING,
  variant = 'compact',
  index = 0,
  statusOnline,
  showStatus = false,
  ctaLabel,
  highlighted = false,
}: ActionTileProps) {
  const isDisabled = disabled || !onClick;
  const isHero = variant === 'hero';
  const labelId = `tile-${id}-label`;
  const descId = `tile-${id}-desc`;

  return (
    <button
      type="button"
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      aria-labelledby={labelId}
      aria-describedby={descId}
      style={{ animationDelay: `${index * 50}ms` }}
      className={`
        group relative flex w-full animate-fade-in-up text-left
        rounded-2xl border bg-white/[0.02]
        transition-all duration-300
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950
        ${ringFocus} ${accent}
        ${isHero
          ? 'min-h-[88px] flex-row items-center gap-3 p-4 shadow-lg shadow-black/20 sm:min-h-[96px] sm:gap-4 sm:p-5'
          : 'h-[132px] flex-col items-start gap-2 p-3 sm:h-[156px] sm:gap-3 sm:p-4'
        }
        ${highlighted && !isDisabled ? 'shadow-md shadow-blue-500/10 ring-1 ring-blue-500/25' : ''}
        ${isDisabled
          ? 'cursor-not-allowed opacity-45'
          : 'cursor-pointer hover:shadow-md hover:shadow-black/25 active:scale-[0.98]'
        }
      `}
    >
      <div
        className={`
          flex shrink-0 items-center justify-center rounded-xl
          transition-transform duration-300 group-hover:scale-105
          ${iconBg}
          ${isHero ? 'h-12 w-12 sm:h-14 sm:w-14' : 'h-9 w-9 sm:h-10 sm:w-10'}
        `}
        aria-hidden="true"
      >
        <Icon className={isHero ? 'h-6 w-6 sm:h-7 sm:w-7' : 'h-4 w-4 sm:h-5 sm:w-5'} />
      </div>

      <div className={`min-w-0 flex-1 ${isHero ? 'pr-8' : 'w-full pb-1'}`}>
        <div className="flex flex-wrap items-center gap-1">
          <p
            id={labelId}
            className={`font-semibold leading-tight text-white ${
              isHero ? 'text-base sm:text-lg' : 'line-clamp-2 text-xs sm:text-sm'
            }`}
          >
            {label}
          </p>
          {badge && (
            <span
              className={`rounded px-1 py-0.5 text-[11px] font-medium ${
                badgeTone === 'action'
                  ? 'bg-orange-500 font-bold text-white animate-pulse'
                  : highlighted
                    ? 'bg-blue-500/20 text-blue-300'
                    : 'bg-white/[0.06] text-slate-500'
              }`}
            >
              {badge}
            </span>
          )}
          {showStatus && statusOnline !== undefined && (
            <span
              className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                statusOnline ? 'animate-pulse bg-emerald-400' : 'bg-slate-600'
              }`}
              aria-label={statusOnline ? 'Online' : 'Offline'}
            />
          )}
        </div>
        <p
          id={descId}
          className={`mt-0.5 leading-snug text-slate-500 ${
            isHero
              ? 'line-clamp-2 text-sm sm:line-clamp-none'
              : 'line-clamp-2 text-[11px] sm:text-xs'
          }`}
        >
          {description}
        </p>
        {isHero && !isDisabled && ctaLabel && (
          <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-orange-400 transition-colors duration-300 group-hover:text-orange-300">
            {ctaLabel}
            <ChevronRight
              className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </span>
        )}
      </div>

      <ChevronRight
        className={`
          absolute text-slate-700 transition-all duration-300
          group-hover:text-slate-400 group-focus-visible:text-slate-400
          ${isHero
            ? 'right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-orange-500/50 group-hover:translate-x-1 group-hover:text-orange-400 sm:right-4'
            : 'bottom-2.5 right-2.5 h-3.5 w-3.5 group-hover:translate-x-0.5 sm:bottom-3 sm:right-3'
          }
        `}
        aria-hidden="true"
      />
    </button>
  );
}
